import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  ApiOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  CloudServerOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DiffOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  GlobalOutlined,
  HeartOutlined,
  HistoryOutlined,
  LinkOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RollbackOutlined,
  SafetyOutlined,
  SettingOutlined,
  ShopOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BarahBrand, BarahWordmark } from '@barahpay/shared';
import { useAuth } from '../auth/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { NAV_ITEMS } from './navigation';
import type { NavItem } from './navigation';

const ICONS: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  'credit-card': <CreditCardOutlined />,
  'file-search': <FileSearchOutlined />,
  link: <LinkOutlined />,
  rollback: <RollbackOutlined />,
  exclamation: <ExclamationCircleOutlined />,
  bank: <BankOutlined />,
  diff: <DiffOutlined />,
  team: <TeamOutlined />,
  sync: <SyncOutlined />,
  'file-text': <FileTextOutlined />,
  appstore: <AppstoreOutlined />,
  shop: <ShopOutlined />,
  api: <ApiOutlined />,
  'cloud-server': <CloudServerOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  heart: <HeartOutlined />,
  'bar-chart': <BarChartOutlined />,
  user: <UserOutlined />,
  safety: <SafetyOutlined />,
  history: <HistoryOutlined />,
  setting: <SettingOutlined />,
};

const GROUP_ORDER: NavItem['group'][] = [
  'overview',
  'money',
  'customers',
  'catalog',
  'platform',
  'administration',
];

export function AdminLayout() {
  const { t } = useTranslation();
  const { user, canAny, signOut } = useAuth();
  const { language, rtl, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Only the sections the user may open are listed; the backend re-checks each request anyway
  // (spec section 65).
  const menuItems = useMemo<MenuProps['items']>(() => {
    const visible = NAV_ITEMS.filter((item) => canAny(item.permissions));

    const entry = (item: NavItem) => ({
      key: item.path,
      icon: ICONS[item.icon],
      label: <Link to={item.path}>{t(`nav.${item.labelKey}`)}</Link>,
    });

    const menu: NonNullable<MenuProps['items']> = [];

    // On the collapsed rail there is no room for a heading — antd would clip it to "Money…", which
    // reads as broken. The icons alone carry the rail, with tooltips for the names.
    if (collapsed) {
      return GROUP_ORDER.flatMap((group) => visible.filter((item) => item.group === group)).map(entry);
    }

    for (const group of GROUP_ORDER) {
      const items = visible.filter((item) => item.group === group);

      if (items.length === 0) {
        continue;
      }

      // The overview group is a single entry, so a group heading would be noise.
      if (group === 'overview') {
        menu.push(...items.map(entry));
        continue;
      }

      menu.push({
        type: 'group',
        key: `group-${group}`,
        label: t(`navGroups.${group}`),
        children: items.map(entry),
      });
    }

    return menu;
  }, [canAny, collapsed, t]);

  // '/payments/PAY-…' should keep the Payments entry highlighted.
  const selectedKey = useMemo(() => {
    const candidates = NAV_ITEMS.map((item) => item.path)
      .filter((path) => path !== '/' && location.pathname.startsWith(path))
      .sort((a, b) => b.length - a.length);

    return candidates[0] ?? '/';
  }, [location.pathname]);

  const CollapseIcon = collapsed === rtl ? MenuFoldOutlined : MenuUnfoldOutlined;

  return (
    /*
     * The Barah "soft floating card" shell: the page is a tinted canvas and the navigation is a rounded
     * card inset from the edges, rather than a full-height panel butted against them. The inset is what
     * makes the sidebar read as an object on the page instead of a browser chrome.
     */
    <Layout style={{ minHeight: '100vh', background: 'var(--bp-background)', padding: 8, gap: 8 }}>
      <Layout.Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={252}
        collapsedWidth={72}
        theme="light"
        style={{
          background: 'transparent',
          // Stays put while a long grid scrolls, so switching screens never needs a scroll back up.
          position: 'sticky',
          insetBlockStart: 8,
          height: 'calc(100vh - 16px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--bp-surface)',
            borderRadius: 'var(--bp-radius-2xl)',
            boxShadow: 'var(--bp-shadow-md)',
            overflow: 'hidden',
          }}
        >
          {/* The brand gradient as a hairline along the top edge of the card. */}
          <div className="bp-accent-bar" />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 64,
              paddingInline: collapsed ? 0 : 16,
              justifyContent: collapsed ? 'center' : 'space-between',
              borderBottom: '1px solid var(--bp-border-light)',
              flexShrink: 0,
            }}
          >
            <BarahBrand size={collapsed ? 32 : 34} markOnly={collapsed} />

            {collapsed ? null : (
              <Button
                type="text"
                size="small"
                aria-label={t('common.close')}
                icon={<CollapseIcon />}
                onClick={() => setCollapsed(true)}
              />
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', paddingBlock: 8 }}>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              items={menuItems}
              inlineIndent={16}
              style={{ borderInlineEnd: 0, background: 'transparent' }}
            />
          </div>

          {/* The corporate signature: this is a Barah product, and the footer says so quietly. */}
          {collapsed ? null : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 16px',
                borderTop: '1px solid var(--bp-border-light)',
                flexShrink: 0,
              }}
            >
              <BarahWordmark height={14} />
            </div>
          )}
        </div>
      </Layout.Sider>

      <Layout style={{ background: 'transparent', gap: 8 }}>
        <Layout.Header
          style={{
            background: 'var(--bp-surface)',
            borderRadius: 'var(--bp-radius-2xl)',
            boxShadow: 'var(--bp-shadow-sm)',
            paddingInline: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          {collapsed ? (
            <Button
              type="text"
              aria-label="Toggle navigation"
              icon={<CollapseIcon />}
              onClick={() => setCollapsed(false)}
            />
          ) : (
            <span />
          )}

          <Space size="small">
            <Tooltip title={t('common.language')}>
              <Button type="text" icon={<GlobalOutlined />} onClick={toggleLanguage}>
                {language === 'ar' ? 'EN' : 'ع'}
              </Button>
            </Tooltip>

            <Dropdown
              menu={{
                items: [
                  {
                    key: 'profile',
                    icon: <UserOutlined />,
                    label: t('nav.users'),
                    onClick: () => navigate('/profile'),
                  },
                  { type: 'divider' },
                  {
                    key: 'signout',
                    icon: <LogoutOutlined />,
                    danger: true,
                    label: t('common.signOut'),
                    onClick: () => void signOut(),
                  },
                ],
              }}
            >
              <Space style={{ cursor: 'pointer', paddingInline: 4 }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ background: 'var(--bp-navy)', color: '#fff' }}
                />
                <span style={{ fontWeight: 500 }}>{user?.fullName}</span>
              </Space>
            </Dropdown>
          </Space>
        </Layout.Header>

        <Layout.Content style={{ background: 'transparent' }}>
          <Outlet />
        </Layout.Content>

        <Layout.Footer
          style={{ textAlign: 'center', padding: '8px 16px 4px', background: 'transparent' }}
        >
          <Typography.Text style={{ fontSize: 12, color: 'var(--bp-text-muted)' }}>
            {t('common.appName')} · {user?.roles.join(', ')}
          </Typography.Text>
        </Layout.Footer>
      </Layout>
    </Layout>
  );
}
